/**
 * Structured Data Composables
 * Functions to generate Schema.org JSON-LD structured data
 */

import {
  absoluteUrl,
  aggregateRating,
  cleanSchema,
  cleanText,
  formatDateISO,
  geoCoordinates,
  imageObject,
  imageObjects,
  organizationSchema,
  postalAddress
} from '~/utils/structuredDataHelpers'

/**
 * Generate Product schema for Kitchen/Living/Complement pages
 * @param {Object} product - Product data
 * @param {string} baseUrl - Base URL
 * @param {string} path - Localized page path (e.g. route.path)
 * @returns {Object} - Product schema
 */
export const useProductSchema = (product, baseUrl, path) => {
  if (!product) return null

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.title,
    'description': cleanText(product.subtitle || product.paragraph || product.abstract),
    'brand': organizationSchema(),
    'url': absoluteUrl(path, baseUrl)
  }

  // Add main image
  const mainImage = getFirst(product.coverMedia || product.image)
  if (mainImage) {
    schema.image = imageObject(mainImage, baseUrl)
  }

  // Add additional images from gallery
  if (product.gallery1 && Array.isArray(product.gallery1)) {
    const galleryImages = product.gallery1
      .map(item => getFirst(item.itemImage))
      .filter(Boolean)

    if (galleryImages.length > 0) {
      const images = imageObjects(galleryImages, baseUrl)
      if (images.length > 0) {
        schema.image = [ schema.image, ...images ].filter(Boolean)
      }
    }
  }

  // Add category
  if (product.kitchenType && Array.isArray(product.kitchenType) && product.kitchenType.length > 0) {
    schema.category = product.kitchenType[0].title
  }

  // Add materials
  if (product.materials && Array.isArray(product.materials) && product.materials.length > 0) {
    schema.material = product.materials.map(m => m.title).filter(Boolean).join(', ')
  }

  // NOTE: no `offers`/price — Veneta Cucine is not an e-commerce, so an Offer
  // without price/priceCurrency would be flagged as invalid by Google.

  return cleanSchema(schema)
}

/**
 * Generate BreadcrumbList schema
 * @param {Array} items - Breadcrumb items [{label, href, current}]
 * @param {string} baseUrl - Base URL
 * @returns {Object} - BreadcrumbList schema
 */
export const useBreadcrumbSchema = (items, baseUrl) => {
  if (!items || !Array.isArray(items) || items.length === 0) return null

  const listItems = items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.label,
    'item': item.href ? absoluteUrl(item.href, baseUrl) : undefined
  }))

  return cleanSchema({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': listItems
  })
}

/**
 * Generate LocalBusiness schema for Store pages
 * @param {Object} store - Store data
 * @param {string} baseUrl - Base URL
 * @param {string} locale - Current locale
 * @returns {Object} - LocalBusiness schema
 */
export const useLocalBusinessSchema = (store, baseUrl, locale) => {
  if (!store) return null

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FurnitureStore',
    '@id': absoluteUrl(`/${locale}/rivenditore/${store.slug}`, baseUrl),
    'name': store.title || store.storeLegalName,
    'description': cleanText(store.aboutUs || store.aboutShowroom)
  }

  // Add image
  const coverImage = getFirst(store.coverImage)
  if (coverImage) {
    schema.image = imageObject(coverImage, baseUrl)
  }

  // Add address
  const address = postalAddress({
    storeAddress: store.storeAddress,
    city: store.city,
    province: store.province,
    zipCode: store.zipCode,
    country: store.foreignCountry?.[0]?.nation || 'IT'
  })
  if (address) {
    schema.address = address
  }

  const [ lat, lng ] = store.location?.coordinates.split(',') ?? []

  // Add geo coordinates
  const geo = geoCoordinates(lat, lng)
  if (geo) {
    schema.geo = geo
  }

  // Add contact info
  if (store.storePhone) {
    schema.telephone = store.storePhone
  }

  if (store.publicContactEmail) {
    schema.email = store.publicContactEmail
  }

  // Add website URL
  if (store.websiteUrl?.url) {
    schema.url = store.websiteUrl.url
  }

  // Add opening hours
  if (store.textOpeningHours) {
    // debugger
    schema.openingHours = store.textOpeningHours
  } else if (store.openingHours) {
    schema.openingHours = store.openingHours
  }

  // Add aggregate rating.
  // Google requires reviewCount on AggregateRating, so we only emit it when we
  // have a real count. We also require a minimum number of reviews: a snippet
  // built on 1-2 reviews (e.g. "5★ from 1 review") is weak and Google may skip
  // it — better to emit nothing than a marginal/penalising rating snippet.
  const MIN_REVIEWS_FOR_RATING = 5
  const reviewCount = parseInt(store.reviewCount)
  if (parseFloat(store.reviewRating) > 0 && reviewCount >= MIN_REVIEWS_FOR_RATING) {
    const rating = aggregateRating(store.reviewRating, reviewCount)
    if (rating) {
      schema.aggregateRating = rating
    }
  }

  // Add social media profiles
  const sameAs = []
  if (store.facebookUrl?.url) sameAs.push(store.facebookUrl.url)
  if (store.instagramUrl?.url) sameAs.push(store.instagramUrl.url)
  if (store.linkedinUrl?.url) sameAs.push(store.linkedinUrl.url)
  if (store.youTubeUrl?.url) sameAs.push(store.youTubeUrl.url)
  if (store.pinterestUrl?.url) sameAs.push(store.pinterestUrl.url)
  if (store.tikTokUrl?.url) sameAs.push(store.tikTokUrl.url)

  if (sameAs.length > 0) {
    schema.sameAs = sameAs
  }

  // Add parent organization
  schema.parentOrganization = organizationSchema()

  return cleanSchema(schema)
}

/**
 * Generate Article schema for News/Magazine pages
 * @param {Object} article - Article data
 * @param {string} baseUrl - Base URL
 * @param {string} locale - Current locale
 * @param {string} section - Section name (news/magazine)
 * @returns {Object} - Article schema
 */
export const useArticleSchema = (article, baseUrl, locale, section = 'magazine') => {
  if (!article) return null

  const categorySlug = article.category?.[0]?.slug || article.categories?.[0]?.slug || ''
  const articleUrl = categorySlug
    ? absoluteUrl(`/${locale}/${section}/${categorySlug}/${article.slug}`, baseUrl)
    : absoluteUrl(`/${locale}/${section}/${article.slug}`, baseUrl)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': cleanText(article.abstract || article.descriptionheronews),
    'url': articleUrl
  }

  // Add author (Veneta Cucine)
  schema.author = organizationSchema()
  schema.publisher = organizationSchema()

  // Add dates
  const publishedDate = formatDateISO(article.date || article.dateCreated)
  if (publishedDate) {
    schema.datePublished = publishedDate
  }

  const modifiedDate = formatDateISO(article.dateUpdated || article.date || article.dateCreated)
  if (modifiedDate) {
    schema.dateModified = modifiedDate
  }

  // Add main image (caption = alt reale, già con fallback al titolo validato dal backend)
  const mainImage = getFirst(article.image || article.thumbnail)
  if (mainImage) {
    schema.image = imageObject(mainImage, baseUrl)
  }

  // Add article section/category
  if (article.category?.[0]?.title || article.categories?.[0]?.title) {
    schema.articleSection = article.category?.[0]?.title || article.categories?.[0]?.title
  }

  // Add article body (if available)
  if (article.htmlContent) {
    schema.articleBody = cleanText(article.htmlContent, 10000)
  }

  return cleanSchema(schema)
}

/**
 * Generate FAQPage schema
 * @param {Array} faqs - Array of FAQ items [{title, description}]
 * @returns {Object} - FAQPage schema
 */
export const useFAQSchema = faqs => {
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) return null

  const mainEntity = faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.title,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': cleanText(faq.description)
    }
  })).filter(item => item.name && item.acceptedAnswer.text)

  if (mainEntity.length === 0) return null

  return cleanSchema({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  })
}

/**
 * Generate WebPage schema (generic page)
 * @param {Object} page - Page data
 * @param {string} baseUrl - Base URL
 * @param {string} locale - Current locale
 * @param {string} path - Current path
 * @returns {Object} - WebPage schema
 */
export const useWebPageSchema = (page, baseUrl, locale, path) => {
  if (!page) return null

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': page.title || page.metaTitle,
    'description': cleanText(page.description || page.metaDescription),
    'url': absoluteUrl(path, baseUrl)
  }

  // Add main image
  const mainImage = getFirst(page.image || page.metaOgImage)
  if (mainImage) {
    schema.image = imageObject(mainImage, baseUrl)
  }

  // Add publisher
  schema.publisher = organizationSchema()

  // Add in language
  schema.inLanguage = locale

  return cleanSchema(schema)
}

/**
 * Generate CollectionPage schema for listing pages
 * @param {Array} items - Array of items
 * @param {string} listName - Name of the list
 * @param {string} baseUrl - Base URL
 * @returns {Object} - ItemList schema
 */
export const useCollectionPageSchema = (items, listName, baseUrl) => {
  if (!items || !Array.isArray(items) || items.length === 0) return null

  const listItems = items.map((item, index) => {
    const listItem = {
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.title
    }

    // Add URL if available
    if (item.slug) {
      listItem.url = absoluteUrl(`/${item.sectionHandle || 'kitchens'}/${item.slug}`, baseUrl)
    }

    // Add image if available
    const itemImage = getFirst(item.coverMedia || item.image || item.thumbnail)
    if (itemImage) {
      listItem.image = imageObject(itemImage, baseUrl)
    }

    return listItem
  })

  return cleanSchema({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': listName,
    'numberOfItems': items.length,
    'itemListElement': listItems
  })
}
